import React, { useState, useMemo, useCallback } from "react";
import { motion } from "framer-motion";
import { db } from "../firebase";
import { doc, updateDoc, deleteDoc } from "firebase/firestore";
import { toast } from "react-toastify";
import "../styles/BookingList.css";

const formatBookingId = (id) => {
  if (!id) return "—";
  return `BCC-${id.slice(0, 6).toUpperCase()}`;
};

const formatPhone = (phone) => {
  if (!phone) return "No phone";
  const cleaned = String(phone).replace(/[^0-9+]/g, "");
  if (cleaned.startsWith("+91")) return cleaned;
  if (cleaned.startsWith("91")) return "+" + cleaned;
  if (cleaned.length === 10) return "+91" + cleaned;
  return cleaned;
};

export default function BookingList({
  bookings,
  isAdmin,
  loading,
  error,
  refetch
}) {
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [expandedBooking, setExpandedBooking] = useState(null);
  const [copiedId, setCopiedId] = useState(null);

  const totalBookings = bookings?.length || 0;

  const pendingCount = useMemo(
    () => bookings.filter((b) => b.status === "pending").length,
    [bookings]
  );

  const confirmedCount = useMemo(
    () => bookings.filter((b) => b.status === "confirmed").length,
    [bookings]
  );

  const completedCount = useMemo(
    () => bookings.filter((b) => b.status === "completed").length,
    [bookings]
  );

  const handleCancel = useCallback(
    async (id) => {
      try {
        await updateDoc(doc(db, "bookings", id), { status: "cancelled" });
        toast.info("Booking cancelled");
      } catch {
        toast.error("Cancel failed");
      }
    },
    []
  );

  const handleStatusUpdate = useCallback(async (id, status) => {
    try {
      await updateDoc(doc(db, "bookings", id), { status });
      toast.success("Status updated");
    } catch {
      toast.error("Status update failed");
    }
  }, []);

  const handleDelete = useCallback(async (id) => {
    try {
      await deleteDoc(doc(db, "bookings", id));
      toast.warn("Booking deleted");
      setDeleteConfirm(null);
    } catch {
      toast.error("Delete failed");
      setDeleteConfirm(null);
    }
  }, []);

  const copyBookingId = useCallback((id) => {
    navigator.clipboard.writeText(id);
    setCopiedId(id);
    toast.success("Booking ID copied");
    setTimeout(() => setCopiedId(null), 2000);
  }, []);

  if (loading) {
    return (
      <div className="booking-list-wrapper">
        <div className="loading-state">
          <div className="spinner" />
          <p>Loading bookings...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="booking-list-wrapper">
        <div className="error-state">
          <p>{error.message}</p>
          {refetch && (
            <button className="btn retry" onClick={refetch}>
              Retry
            </button>
          )}
        </div>
      </div>
    );
  }

  if (!bookings || bookings.length === 0) {
    return (
      <div className="booking-list-wrapper">
        <div className="empty-state">
          <p>No bookings found</p>
          {!isAdmin && <p>Create your first booking from services</p>}
        </div>
      </div>
    );
  }

  return (
    <>
      {deleteConfirm && (
        <motion.div
          className="delete-modal-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          onClick={() => setDeleteConfirm(null)}
        >
          <motion.div
            className="delete-modal"
            initial={{ scale: 0.9 }}
            animate={{ scale: 1 }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3>Delete Booking</h3>
            <p>
              Booking ID: <strong>{formatBookingId(deleteConfirm.id)}</strong>
            </p>
            <p>This action cannot be undone.</p>
            <div className="modal-actions">
              <button
                className="btn cancel"
                onClick={() => setDeleteConfirm(null)}
              >
                Cancel
              </button>
              <button
                className="btn delete confirm-delete"
                onClick={() => handleDelete(deleteConfirm.id)}
              >
                Delete
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}

      <div className="booking-list-wrapper">
        {isAdmin && (
          <div className="admin-toolbar">
            <div className="admin-stats">
              <span>Total: {totalBookings}</span>
              <span>Pending: {pendingCount}</span>
              <span>Confirmed: {confirmedCount}</span>
              <span>Completed: {completedCount}</span>
            </div>
          </div>
        )}

        <div className="booking-list">
          {bookings.map((b, index) => {
            const formattedPhone = formatPhone(b.phone || b.mobile);
            const isExpanded = expandedBooking === b.id;
            const status = b.status || "pending";

            return (
              <motion.div
                key={b.id}
                className={`booking-card ${isExpanded ? "expanded" : ""}`}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.03 }}
                onClick={() =>
                  setExpandedBooking(isExpanded ? null : b.id)
                }
              >
                {/* Booking ID bar (icon handled in JSX/CSS separately) */}
                <div className="booking-id-bar">
                  <span className="booking-id-label">Booking ID</span>
                  <span className="booking-id-value">
                    {formatBookingId(b.id)}
                  </span>
                  <button
                    className="copy-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      copyBookingId(b.id);
                    }}
                  >
                    {copiedId === b.id ? "Copied" : "Copy"}
                  </button>
                </div>

                <div className="booking-info">
                  <h3>{b.customerName || b.name || "Customer"}</h3>

                  {/* enhanced with classes only; logic unchanged */}
                  <p className="detail-row service">
                    <span className="label">Service</span>
                    <span className="value">
                      {b.service || "Not specified"}
                    </span>
                  </p>

                  <p className="detail-row date">
                    <span className="label">Date</span>
                    <span className="value">
                      {b.date
                        ? new Date(b.date).toLocaleDateString("en-IN")
                        : "Not set"}
                    </span>
                  </p>

                  <p className="detail-row time">
                    <span className="label">Time</span>
                    <span className="value">{b.time || "Not set"}</span>
                  </p>

                  <p className="detail-row status-row">
                    <span className="label">Status</span>
                    <span className="value">
                      <span className={`status ${status}`}>
                        {status}
                      </span>
                    </span>
                  </p>

                  <p className="detail-row phone">
                    <span className="label">Phone</span>
                    <span className="value">
                      <a
                        className="phone-link"
                        href={`tel:${formattedPhone}`}
                        onClick={(e) => e.stopPropagation()}
                      >
                        {formattedPhone}
                      </a>
                    </span>
                  </p>

                  {isExpanded && (
                    <>
                      <p className="detail-row notes">
                        <span className="label">Notes</span>
                        <span className="value">
                          {b.notes || "No additional notes"}
                        </span>
                      </p>

                      {isAdmin && (
                        <>
                          <p className="detail-row customer">
                            <span className="label">User Email</span>
                            <span className="value">
                              {b.userEmail || b.email || "N/A"}
                            </span>
                          </p>
                          <p className="detail-row customer">
                            <span className="label">User ID</span>
                            <span className="value">
                              {b.userId
                                ? b.userId.slice(0, 12) + "…"
                                : "N/A"}
                            </span>
                          </p>
                        </>
                      )}
                    </>
                  )}
                </div>

                <div className="booking-actions">
                  {!isAdmin && status === "pending" && (
                    <button
                      className="btn cancel"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleCancel(b.id);
                      }}
                    >
                      Cancel
                    </button>
                  )}

                  {isAdmin && (
                    <>
                      <select
                        className="status-dropdown"
                        value={status}
                        onChange={(e) =>
                          handleStatusUpdate(b.id, e.target.value)
                        }
                        onClick={(e) => e.stopPropagation()}
                      >
                        <option value="pending">Pending</option>
                        <option value="confirmed">Confirmed</option>
                        <option value="completed">Completed</option>
                        <option value="cancelled">Cancelled</option>
                      </select>

                      <button
                        className="btn delete"
                        onClick={(e) => {
                          e.stopPropagation();
                          setDeleteConfirm(b);
                        }}
                      >
                        Delete
                      </button>
                    </>
                  )}
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </>
  );
}
